
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import useTranslation from 'next-translate/useTranslation';
import { cachedDataWrapper } from '../lib/dataFetcher';
import { getHourlyWaitAverages } from '../lib/dbFetcher';
import SecurityOpeningNote from '../components/SecurityOpeningNote';
const HourlyWaitChart = dynamic(() => import('../components/HourlyWaitChart'), {
    ssr: false
});
const CPH_SOURCE_URL = {
    en: 'https://www.cph.dk/en/practical/check-in',
    da: 'https://www.cph.dk/praktisk/check-in'
};
async function getStaticProps() {
    try {
        const [data, hourlyAverages] = await Promise.all([
            cachedDataWrapper(),
            getHourlyWaitAverages()
        ]);
        return {
            props: { initialData: data, hourlyAverages },
            revalidate: 60
        };
    }
    catch (error) {
        console.error('Error building opening-hours page:', error);
        return {
            props: { initialData: {}, hourlyAverages: [] },
            revalidate: 60
        };
    }
}
function SecurityOpeningHours({ initialData, hourlyAverages }) {
    const { t, lang } = useTranslation('opening-hours');
    const [data, setData] = useState(initialData);
    const fetchData = async () => {
        try {
            const response = await fetch('/api/fetchData');
            if (!response.ok)
                throw Error(response.statusText);
            setData(await response.json());
        }
        catch {
            // keep last good data
        }
    };
    useEffect(() => {
        fetchData();
        const intervalId = setInterval(fetchData, 1000 * 60);
        return () => clearInterval(intervalId);
    }, []);
    const sourceUrl = CPH_SOURCE_URL[lang] || CPH_SOURCE_URL.en;
    const faqs = [
        { q: t('faq_q1'), a: t('faq_a1') },
        { q: t('faq_q2'), a: t('faq_a2') },
        { q: t('faq_q3'), a: t('faq_a3') }
    ];
    const faqJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(({ q, a }) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: { '@type': 'Answer', text: a }
        }))
    };
    return (<div className="bg-white p-4 sm:p-6 my-auto rounded max-w-3xl mx-auto text-gray-800">
            <Head>
                <title>{t('meta_title')}</title>
                <meta name="description" content={t('meta_description')}/>
                <meta name="keywords" content={t('meta_keywords')}/>
                <meta name="robots" content="all"/>
                <meta property="og:title" content={t('meta_title')}/>
                <meta property="og:description" content={t('meta_description')}/>
                <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqJsonLd)
        }}/>
            </Head>

            <h1 className="text-2xl sm:text-3xl font-bold">
                {t('page_title')}
            </h1>
            <p className="mt-3 text-gray-600">{t('intro')}</p>

            {/* Live, schedule-derived status */}
            <section className="mt-6">
                <h2 className="text-xl font-semibold">
                    {t('live_status_heading')}
                </h2>
                <SecurityOpeningNote securityOpening={data?.securityOpening} forceShow/>
            </section>

            {/* Authoritative quote */}
            <section className="mt-8">
                <h2 className="text-xl font-semibold">
                    {t('official_heading')}
                </h2>
                <blockquote className="mt-2 border-l-4 border-[var(--primary)] bg-gray-50 p-4 italic">
                    “{t('official_quote')}”
                </blockquote>
                <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-sm text-[var(--secondary)] underline">
                    {t('source')}
                </a>
            </section>

            {/* Explainer */}
            <section className="mt-8">
                <h2 className="text-xl font-semibold">
                    {t('explainer_heading')}
                </h2>
                <p className="mt-2">{t('explainer_p1')}</p>
                <p className="mt-2">{t('explainer_p2')}</p>
                <ul className="mt-2 list-disc list-inside text-gray-700">
                    <li>{t('explainer_express')}</li>
                    <li>{t('explainer_passport')}</li>
                </ul>
            </section>

            {/* Arrival recommendation */}
            <section className="mt-8">
                <h2 className="text-xl font-semibold">
                    {t('arrival_heading')}
                </h2>
                <p className="mt-2">{t('arrival_body')}</p>
            </section>

            {/* Quietest times chart */}
            <section className="mt-8">
                <h2 className="text-xl font-semibold">
                    {t('chart_heading')}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                    {t('chart_caption')}
                </p>
                <div className="mt-4">
                    <HourlyWaitChart data={hourlyAverages}/>
                </div>
            </section>

            {/* FAQ */}
            <section className="mt-8">
                <h2 className="text-xl font-semibold">{t('faq_heading')}</h2>
                <dl className="mt-2 divide-y divide-gray-200">
                    {faqs.map(({ q, a }, i) => (<div key={i} className="py-3">
                            <dt className="font-semibold">{q}</dt>
                            <dd className="mt-1 text-gray-700">{a}</dd>
                        </div>))}
                </dl>
            </section>

            <div className="mt-8">
                <Link href="/" className="text-[var(--secondary)] underline">
                    {t('common:security_wait_times')}
                </Link>
            </div>
        </div>);
}
export default SecurityOpeningHours;

    async function __Next_Translate__getStaticProps__1a1723ec699__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/security-opening-hours',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1723ec699__ as getStaticProps }
  